import { AxiosRequestConfig } from 'utils/gql/axios/AxiosClient';
import { GqlError, GqlHeaders, GqlResponse } from 'utils/gql/gql.types';
import { ApolloError, DocumentNode, gql } from '@apollo/client';
import { OperationDefinitionNode, OperationTypeNode } from 'graphql/language/ast';
import { getOperationName } from '@apollo/client/utilities';
import { ServerError } from '@apollo/client/link/utils';
import { toGqlResponse } from 'utils/gql/apollo/toGqlResponse';
import { GqlResponseError } from 'utils/gql/errors/GqlResponseError';
import { FETCH_POLICY } from 'common/apollo/FETCH_POLICY';
import { apolloClient, omitTypename } from './ApolloClient';

export async function apolloGql<T>(
  query: string,
  variables = {} as any,
  options?: Partial<AxiosRequestConfig>,
  headerOptions?: GqlHeaders,
  dateHeaderOptions?: GqlHeaders,
  allowErrors?: boolean,
  batch?: boolean
): Promise<GqlResponse<T>> {
  const apolloDocumentNode = gql(query);
  const operationName = getOperationName(apolloDocumentNode) ?? 'UNDEFINED';
  const operationType = getOperationType(apolloDocumentNode);
  const vars = JSON.parse(JSON.stringify(variables), omitTypename);
  const headers = { ...headerOptions, ...dateHeaderOptions };
  try {
    if (operationType === 'query') {
      return toGqlResponse(
        await apolloClient.query<T>({
          query: apolloDocumentNode,
          variables: vars,
          errorPolicy: allowErrors ? 'all' : 'none',
          fetchPolicy: FETCH_POLICY.NO_CACHE,
          context: {
            headers,
            batch
          }
        }),
        operationName
      );
    }
    return toGqlResponse(
      await apolloClient.mutate<T>({
        mutation: apolloDocumentNode,
        variables: vars,
        errorPolicy: allowErrors ? 'all' : 'none',
        fetchPolicy: FETCH_POLICY.NO_CACHE,
        context: {
          headers,
          batch
        }
      }),
      operationName
    );
  } catch (e: unknown) {
    const clientError = e as ApolloError;

    if (allowErrors) {
      return toGqlResponse(clientError, operationName);
    }
    const statusCode = (clientError?.networkError as ServerError)?.statusCode;

    if (clientError.graphQLErrors?.length) {
      // eslint-disable-next-line @typescript-eslint/no-throw-literal
      throw new GqlResponseError(clientError.graphQLErrors as GqlError[], operationName, statusCode);
    }

    if (clientError.clientErrors?.length) {
      // eslint-disable-next-line @typescript-eslint/no-throw-literal
      throw new GqlResponseError(
        clientError.clientErrors.map((it) => ({ message: it.stack ?? it.message })),
        operationName,
        statusCode
      );
    }

    if (clientError.networkError) {
      // eslint-disable-next-line @typescript-eslint/no-throw-literal
      throw new GqlResponseError(
        [{ message: clientError.networkError.stack ?? clientError.networkError.message }],
        operationName,
        statusCode
      );
    }

    // thrown literal to get unified response
    // eslint-disable-next-line @typescript-eslint/no-throw-literal
    throw new GqlResponseError(clientError.graphQLErrors as GqlError[], operationName, statusCode);
  }
}

const getOperationType = (documentNode: DocumentNode): OperationTypeNode =>
  (documentNode.definitions?.[0] as OperationDefinitionNode)?.operation ?? 'query';
